import {
  abbreviateId,
  CARE_CANCEL_REPLACE_REQUEST,
  CARE_NEW_ORDER_SINGLE,
  CARE_ORDER,
  CareOrderStatusEnum,
  formattedDateForSubscription,
  formattedDay,
  logger,
  NotificationVariants,
  SizeModeEnum,
  type CareCancelReplaceRequest,
  type CareNewOrderSingle,
  type CareOrder,
  type IWebSocketClient,
  type SideEnum,
  type ToastProps,
} from '@talos/kyoko';
import { sub } from 'date-fns';
import { findLast } from 'lodash-es';
import { Observable, type Subscription } from 'rxjs';
import { v4 as uuid } from 'uuid';
import type { AdjustedSettlementDateResponse } from '../../../containers/Blotters/CareOrders/adjustedSettlementDateApi';
import type { AvaAppStore } from '../../../providers/AppStateProvider/AppStateProviderHooks';
import { primeNewRFQForm } from '../NewRFQ/RFQSlice';
import { careOrderToPrimeRFQParams } from '../NewRFQ/utils';
import { openView } from '../OMSSlice';
import { OMSView } from '../OMSView';
import { cleanState, clearState, setIsLoading, stringCareOrderCurrency } from './CareOrderSlice';
import { UNITS_CURRENCY, type CareOrderCurrency, type CareOrderFormState, type CareOrderState } from './types';

export class CareOrderService {
  private subscriptions: Subscription[] = [];

  constructor(
    private store: AvaAppStore,
    private webSocketClient: IWebSocketClient<unknown>,
    private addToast: (props: ToastProps) => void
  ) {}

  private observeRequest(clOrdID: string): Observable<CareOrder> {
    return new Observable<CareOrder>(observer => {
      const careOrderRequest = {
        name: CARE_ORDER,
        tag: 'CareOrderService.observeCareOrder',
        // Include start date to improve DB query response times
        StartDate: formattedDateForSubscription(sub(new Date(), { days: 1 })),
        ClOrdID: clOrdID,
      };
      const address = uuid();
      this.webSocketClient.registerSubscription(address, [careOrderRequest], (err, json) => {
        if (err) {
          observer.error(err);
          return;
        }
        const orders = json?.data as CareOrder[];
        if (orders) {
          const latest = findLast(orders ?? [], careOrder => careOrder.ClOrdID === clOrdID);
          if (latest) {
            observer.next(latest);
            observer.complete();
          }
        }
      });
      return () => this.webSocketClient.unregisterSubscription(address);
    });
  }

  public requestOrder(primeRFQ?: boolean): void {
    // Mostly for typechecking, but also safety I guess, grab out the must-have properties here and return early with error if they're not present.
    // Shouldn't happen.
    const symbol = this.formState.symbolField.value?.Symbol;
    const side = this.formState.sideField.value;
    const quantity = this.formState.quantityField.value;
    const currency = this.formState.careOrderCurrencyField.value;
    if (symbol == null || side == null || quantity == null || currency == null) {
      this.addToast({
        text: `An unexpected error occurred, unable to create order request. Please try again, or reach out to Talos support.`,
        variant: NotificationVariants.Negative,
        timeout: 5000,
        dismissable: true,
      });
      logger.error(new Error('Unexpected: user was unable to request care order'), {
        extra: { symbol, side, quantity, currency },
      });
      return;
    }

    this.store.dispatch(setIsLoading(true));
    const clOrdID = uuid();
    const subscription = this.observeRequest(clOrdID).subscribe({
      next: order => {
        this.debug('careOrderReceived', order);
        this.store.dispatch(setIsLoading(false));
        switch (order.status) {
          case CareOrderStatusEnum.New:
          case CareOrderStatusEnum.Working:
          case CareOrderStatusEnum.PartiallyFilled:
          case CareOrderStatusEnum.Filled:
            this.addToast({
              text: `Care order #${abbreviateId(order.OrderID)} ${order.Revision === 1 ? 'created' : 'modified'}.`,
              variant: NotificationVariants.Positive,
              timeout: 5000,
            });

            if (this.state.careOrderBeingModified) {
              // On modification success, we clear (completely wipe) the state.
              this.store.dispatch(clearState());
            } else {
              // On normal care order submission, we keep the form open and just clean (selectively clear) the form if the user wants to submit more similar orders.
              this.store.dispatch(cleanState());
            }

            if (primeRFQ) {
              // If the user clicked "Place & Prime", we open the RFQ Form and immediately prime with details from our care order.
              this.store.dispatch(openView(OMSView.RFQForm));
              this.store.dispatch(primeNewRFQForm(careOrderToPrimeRFQParams(order)));
            }

            break;
          case CareOrderStatusEnum.Rejected:
            this.addToast({
              text: order.Text || 'Care Order rejected with unknown reason. Please contact Talos support.',
              variant: NotificationVariants.Warning,
              timeout: 5000,
            });
            break;
          default:
            logger.error(new Error(`Received unexpected order status "${order.status}" when placing Care Order.`), {
              extra: {
                order,
              },
            });
            this.addToast({
              text:
                order.Text ||
                `Received unexpected order status "${order.status}"  when placing Care Order. Please contact Talos support.`,
              variant: NotificationVariants.Warning,
              timeout: 5000,
            });
            break;
        }
      },
      error: error => {
        logger.error(error);
        this.store.dispatch(setIsLoading(false));
        this.addToast({
          text: 'Failed to create Care Order. Please contact Talos support.',
          variant: NotificationVariants.Negative,
          timeout: 5000,
        });
      },
    });

    const request = buildCareOrderRequest({
      clOrdID,
      symbol,
      side,
      quantity,
      currency,
      group: this.formState.groupField.value,
      comments: this.formState.commentsField.value,
      externalComments: this.formState.externalCommentsField.value,
      counterparty: this.formState.counterpartyField.value?.Name,
      externalGroup: this.formState.externalIDField.value,
      fund: this.formState.fundField.value,
      initiatingFrom: this.formState.initiatingFromField.value,
      settleDate: this.formState.settleDateField.value,
      subAccount: this.formState.allocations.at(0)?.subAccountField.value, // we only support sub acc, not allocs, so this is ok

      careOrderBeingModified: this.state.careOrderBeingModified,

      passFundUnitsAsOrderQty: this.state.referenceData.settings.useFundDefinitionAndTradableIndexesStreams,
      supportAdjustedSettlementDate: this.state.referenceData.settings.supportAdjustedSettlementDate,
      adjustedSettlementDates: this.state.referenceData.adjustedSettlementDates.settlementDates,
    });
    this.debug('requestCareOrder', request);
    this.webSocketClient.registerPublication(request);
    this.subscriptions.push(subscription);
  }

  private get state(): CareOrderState {
    return this.store.getState().careOrder;
  }

  private get formState(): CareOrderFormState {
    return this.store.getState().careOrder.form;
  }

  private debug(key: string, value: any) {
    // dispatch an action so it's logged and can be debugged in the redux debugger but isn't actually handled by reducer and does not affect state in any ways
    this.store.dispatch({ type: `careOrder/debug/${key}`, payload: value });
  }

  public destroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }
}

/**
 * All the inputs for building a CareNewOrderSingle / CareCancelReplaceRequest. Does not allow any
 * param to not be specified just for explicitness. If some field is not relevant to your usage, pass undefined explicitly.
 */
interface BuildCareOrderRequestParams {
  clOrdID: string;
  symbol: string | undefined; // can be undefined in the fund case
  side: SideEnum;
  quantity: string;
  currency: CareOrderCurrency;
  group: string | undefined;
  comments: string | undefined;
  externalComments: string | undefined;
  counterparty: string | undefined;
  externalGroup: string | undefined;
  fund: string | undefined;
  initiatingFrom: string | undefined;
  settleDate: string | undefined;
  subAccount: string | undefined;

  careOrderBeingModified: CareOrder | undefined;

  /** Backward compat setting. Set to true to use the new fund code path where we always send fund units on top-level OrderQty instead of Annotation.BasketOrder.BasketOrderQty. Added in 2.55.0. */
  passFundUnitsAsOrderQty: boolean;
  /** Whether the support adjusted settlement date feature flag is enabled. */
  supportAdjustedSettlementDate?: boolean;
  /** Available settlement dates for the selected settlement date. Used to determine the Annotations.SettleOffset. */
  adjustedSettlementDates?: AdjustedSettlementDateResponse[];
}

/**
 * Builds a request object to be used in WebsocketClient.registerPublication.
 *
 * Centralized logic for creating new care orders, and building modification requests for existing care orders.
 */
export function buildCareOrderRequest({
  clOrdID,
  symbol,
  side,
  group,
  comments,
  externalComments,
  counterparty,
  externalGroup,
  currency,
  fund,
  quantity,
  initiatingFrom,
  settleDate,
  subAccount,

  careOrderBeingModified,

  passFundUnitsAsOrderQty,
  supportAdjustedSettlementDate,
  adjustedSettlementDates,
}: BuildCareOrderRequestParams) {
  const isInUnits = currency === UNITS_CURRENCY;
  const passQtyInBasketAnnotation = isInUnits && fund != null && !passFundUnitsAsOrderQty;

  const request: CareNewOrderSingle | CareCancelReplaceRequest = {
    ClOrdID: clOrdID,
    Side: side,
    Group: group,
    Comments: comments,
    Counterparty: counterparty,
    ExternalGroup: externalGroup,
    ExternalComments: externalComments,
    SubAccount: subAccount,
    TransactTime: formattedDateForSubscription(new Date()),
  };

  if (fund != null) {
    // When placing an order for a fund, we pass the BasketSymbol via this Annotations blob (always)
    request.Annotations = {
      ...request.Annotations,
      BasketOrder: {
        ...careOrderBeingModified?.Annotations?.BasketOrder, // make sure we don't clear any values doing this if we're modifying
        BasketSymbol: fund,
        BasketOrderQty: passQtyInBasketAnnotation ? quantity : undefined,
      },
    };
  }

  if (!passQtyInBasketAnnotation) {
    // If the qty isn't being passed in the Annotation.BasketOrder object, then we are using the top-level attributes to convey the OrderQty
    request.Symbol = symbol;
    request.OrderQty = quantity; // a token qty, or units qty
    request.Currency = stringCareOrderCurrency(currency); // either the selected base/quote or undefined for the units case
    request.SizeMode = isInUnits ? SizeModeEnum.Units : undefined;
  }

  // If we're modifying an existing Care Order, slap on a few extra properties
  if (careOrderBeingModified) {
    (request as CareCancelReplaceRequest).OrderID = careOrderBeingModified.OrderID;

    if (passQtyInBasketAnnotation) {
      // In the case where we set the OrderQty in the BasketOrder Annotation, and we're modifying, the backend will have converted our BasketOrderQty to a top-level OrderQty.
      // We need to set this again so we don't lose it during modification.
      request.Symbol = careOrderBeingModified.Symbol;
      request.Currency = careOrderBeingModified.Currency;
      request.OrderQty = careOrderBeingModified.OrderQty;
    }
  }

  if (initiatingFrom) {
    request.Annotations = {
      ...request.Annotations,
      ETP: {
        InitiatingFrom: initiatingFrom,
      },
    };
  }

  if (settleDate) {
    request.SettleDate = settleDate;
  }

  if (settleDate && supportAdjustedSettlementDate && adjustedSettlementDates && adjustedSettlementDates.length > 0) {
    const adjustedSettlementDate = adjustedSettlementDates.find(
      sd => formattedDay(sd.AdjustedDate) === formattedDay(settleDate)
    );
    if (adjustedSettlementDate) {
      request.Annotations = {
        ...request.Annotations,
        SettleOffset: `${adjustedSettlementDate.numericOffset}`,
      };
    }
  }

  return { type: careOrderBeingModified ? CARE_CANCEL_REPLACE_REQUEST : CARE_NEW_ORDER_SINGLE, data: [request] };
}
